import { useState, useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lightbulb, CheckCircle2, AlertTriangle, Zap, ChevronDown, FlaskConical, HelpCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface ConflictSummary {
  strategyA?: string;
  strategyB?: string;
}

interface Phase13HypothesisInputProps {
  onValueChange: (value: string) => void;
  previousConflict?: ConflictSummary;
  detectedTensions?: string[];
  className?: string;
}

type HypothesisQuality = 'none' | 'weak' | 'medium' | 'strong';

const MAX_CHARS = 200;
const CHAR_WARNING_THRESHOLD = 180;

export const Phase13HypothesisInput = ({
  onValueChange,
  previousConflict,
  detectedTensions,
  className
}: Phase13HypothesisInputProps) => {
  const [hypothesis, setHypothesis] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    onValueChange(hypothesis);
  }, [hypothesis, onValueChange]);

  // Hypothesis quality check
  const hypothesisAnalysis = useMemo(() => {
    const trimmed = hypothesis.trim().toLowerCase();
    
    if (trimmed.length < 15) {
      return { quality: 'none' as HypothesisQuality, tip: null };
    }

    // Check for vague terms
    const vagueTerms = ['vielleicht', 'eventuell', 'könnte sein', 'irgendwie', 'möglicherweise'];
    const hasVague = vagueTerms.some(term => trimmed.includes(term));

    // Check for hypothesis structure (wenn-dann, condition-outcome)
    const structureTerms = ['wenn', 'dann', 'falls', 'sobald', 'annahme', 'voraussetzung', 'bedingung', 'davon aus'];
    const hasStructure = structureTerms.some(term => trimmed.includes(term));

    // Check for falsifiability markers
    const falsifiableTerms = ['widerlegt', 'falsch', 'nicht eintritt', 'scheitert', 'zeigt sich', 'erkennbar', 'messbar', 'sichtbar'];
    const isFalsifiable = falsifiableTerms.some(term => trimmed.includes(term));

    // Check for concrete subject
    const concreteTerms = ['kunden', 'nutzer', 'markt', 'team', 'produkt', 'umsatz', 'wachstum', 'conversion', 'retention'];
    const hasConcrete = concreteTerms.some(term => trimmed.includes(term));

    // Determine quality
    if (hasVague && !hasStructure) {
      return { 
        quality: 'weak' as HypothesisQuality, 
        tip: 'Ersetze "vielleicht" durch eine prüfbare Wenn-Dann-Struktur.'
      };
    }
    if (!hasStructure && trimmed.length < 40) {
      return { 
        quality: 'weak' as HypothesisQuality, 
        tip: 'Formuliere als: "Ich gehe davon aus, dass X, weil Y."'
      };
    }
    if (hasStructure && (isFalsifiable || hasConcrete)) {
      return { quality: 'strong' as HypothesisQuality, tip: null };
    }
    if (hasStructure || hasConcrete) {
      return { 
        quality: 'medium' as HypothesisQuality, 
        tip: 'Woran erkennst du, ob die Annahme stimmt?'
      };
    }
    
    return { 
      quality: 'medium' as HypothesisQuality, 
      tip: 'Konkretisiere: Welche Annahme muss zutreffen?'
    };
  }, [hypothesis]);

  const hasContent = hypothesis.trim().length > 15;

  return (
    <div className={cn("space-y-5", className)}>
      {/* Scharfe Führung - kein Erklären */}
      <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <FlaskConical className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-1">
              Welche zentrale Marktannahme muss zutreffen, damit deine Fokus-Entscheidung richtig ist?
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Diese Annahme erklärt, warum die Kosten der Nicht-Entscheidung akzeptabel sind.
            </p>
          </div>
        </div>
      </div>

      {/* Context from previous conflict */}
      {detectedTensions && detectedTensions.length > 0 && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/30 rounded-lg py-3 px-4 border border-border/30">
          <Zap className="w-4 h-4 text-primary flex-shrink-0" />
          <span>
            <span className="text-muted-foreground">Spannungsfeld:</span>{' '}
            <span className="font-medium text-foreground">{detectedTensions.join(' · ')}</span>
          </span>
        </div>
      )}

      {/* Main hypothesis card */}
      <div className={cn(
        "p-5 rounded-xl border-2 transition-all",
        isFocused 
          ? "border-primary bg-primary/5 shadow-lg shadow-primary/10" 
          : "border-border bg-card/50"
      )}>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
              <FlaskConical className="w-5 h-5 text-primary" />
            </div>
            <div>
              <Label className="text-base font-semibold text-foreground block">
                Deine Marktannahme
              </Label>
              <span className="text-sm text-muted-foreground">
                Ohne diese Annahme ist der Fokus falsch
              </span>
            </div>
          </div>
          
          {/* Single input field */}
          <div className="relative">
            <Input
              value={hypothesis}
              onChange={(e) => setHypothesis(e.target.value.slice(0, MAX_CHARS))}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder="z.B. Enterprise-Kunden priorisieren Compliance über Geschwindigkeit, weil..."
              className={cn(
                "pr-14 text-base bg-background border-2 h-12",
                "placeholder:text-muted-foreground/50",
                isFocused ? "border-primary" : "border-border/50"
              )}
            />
            {hypothesis.length > CHAR_WARNING_THRESHOLD && (
              <span className={cn(
                "absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium",
                hypothesis.length >= MAX_CHARS ? "text-destructive" : "text-muted-foreground"
              )}>
                {hypothesis.length}/{MAX_CHARS}
              </span>
            )}
          </div>

          {/* Falsification nudge */}
          <div className="p-3 bg-muted/30 rounded-lg border border-border/30">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Diese Annahme ist falsch, wenn:</span>{' '}
              <span className="italic">z.B. weniger als 30% der Zielkunden Compliance als Top-3-Priorität nennen.</span>
            </p>
          </div>
        </div>
      </div>

      {/* Quality indicator */}
      {hasContent && (
        <div className={cn(
          "p-4 rounded-xl border-2 transition-all animate-in fade-in slide-in-from-bottom-2 duration-200",
          hypothesisAnalysis.quality === 'strong' && "bg-emerald-500/10 border-emerald-500/40",
          hypothesisAnalysis.quality === 'medium' && "bg-amber-500/10 border-amber-500/40",
          hypothesisAnalysis.quality === 'weak' && "bg-destructive/10 border-destructive/40",
        )}>
          <div className="flex items-start gap-3">
            {hypothesisAnalysis.quality === 'strong' && (
              <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
            )}
            {hypothesisAnalysis.quality === 'medium' && (
              <Lightbulb className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            )}
            {hypothesisAnalysis.quality === 'weak' && (
              <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1 min-w-0">
              <p className={cn(
                "text-base font-semibold",
                hypothesisAnalysis.quality === 'strong' && "text-emerald-700 dark:text-emerald-300",
                hypothesisAnalysis.quality === 'medium' && "text-amber-700 dark:text-amber-300",
                hypothesisAnalysis.quality === 'weak' && "text-destructive",
              )}>
                {hypothesisAnalysis.quality === 'strong' && "✓ Prüfbare Hypothese"}
                {hypothesisAnalysis.quality === 'medium' && "Fast prüfbar – noch einen Schritt"}
                {hypothesisAnalysis.quality === 'weak' && "Noch nicht prüfbar"}
              </p>
              {hypothesisAnalysis.tip && (
                <p className="text-sm text-muted-foreground mt-1">
                  {hypothesisAnalysis.tip}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Thinking aid - collapsed by default */}
      <Collapsible>
        <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group mx-auto py-2 px-4 rounded-lg hover:bg-muted/30">
          <HelpCircle className="w-4 h-4" />
          <span className="font-medium">Was ist eine gute Hypothese?</span>
          <ChevronDown className="w-4 h-4 transition-transform group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4 animate-in slide-in-from-top-2 duration-200">
          <div className="p-5 bg-muted/20 border border-border/40 rounded-xl space-y-4">
            <div>
              <p className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Typische Denkfehler
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-destructive">✗</span>
                  <span>„Ich glaube, es funktioniert" – keine Hypothese, nur ein Gefühl</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive">✗</span>
                  <span>Ohne Falsifizierungsbedingung = Wunschdenken</span>
                </li>
              </ul>
            </div>
            
            <div className="pt-3 border-t border-border/30">
              <p className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                Gute Struktur
              </p>
              <p className="text-sm text-muted-foreground bg-background/50 p-3 rounded-lg border border-border/30 italic">
                „Ich gehe davon aus, dass <span className="text-foreground font-medium">[Subjekt]</span>{' '}
                <span className="text-foreground font-medium">[Verhalten zeigt]</span>, weil{' '}
                <span className="text-foreground font-medium">[Grund]</span>."
              </p>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
